import {
  type ApiScopeRequirement,
  meetsApiScopeRequirement,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import type { GlobalStateMergedState } from 'components/GlobalState/GlobalStateModel';
import { isScopeAllowed } from 'components/GlobalState/helpers/isScopeAllowed';
import { selectApiScopeStringSet } from 'components/GlobalState/selectors/selectApiScopeStringSet';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';

export type MeetsApiScopeFunction = (
  requirement: ApiScopeRequirement,
) => boolean;

/**
 * Allow using api scope conditions in global state selectors.
 *
 * Note: for direct usage in components you can use shortcut `useMeetsApiScope` hook.
 *
 * @example
 * const ctxProps = useGlobalStateContextSelectors({
 *   meetsApiScope: selectMeetsApiScope,
 * });
 *
 * const foosAndBarsScope = {or: [
 *   {method: 'POST', path: '/foo'},
 *   {method: 'POST', path: '/bar'},
 * ]};
 *
 * return (
 *   <div>
 *     Hi all
 *     {ctxProps.meetsApiScope(foosAndBarsScope) && ', especially foos and bars'}
 *     !
 *   </div>
 * );
 * requirement={}
 */
export const selectMeetsApiScope: (
  s: GlobalStateMergedState,
) => MeetsApiScopeFunction = createScopedSelector(() =>
  createSelector(
    selectApiScopeStringSet,
    prop('auth', 'admin'),
    (apiScopeStringSet, admin) => (requirement: ApiScopeRequirement) =>
      admin ||
      meetsApiScopeRequirement(isScopeAllowed(apiScopeStringSet))(requirement),
  ),
);
